import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { renderToStaticMarkup } from "react-dom/server";
import { toast } from "sonner";
import { Share2, Download, Users } from "@/lib/icons";
import { api } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { GardenPlant } from "@/components/client/Garden";
import { ModalClose } from "@/components/ui/ModalClose";

const QUOTES = ["Small daily waterings grow the whole garden.", "You showed up, again and again. This is what that looks like.", "Roots first, then height.", "Growth you can see because you kept going."];

function ShareCard({ name, level, first }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 340" width="600" height="340" style={{ fontFamily: "Georgia, serif" }}>
      <rect width="600" height="340" rx="28" fill="#f0f7f1" />
      <rect x="18" y="18" width="564" height="304" rx="22" fill="#ffffff" stroke="#bfe3c6" />
      <g transform="translate(40 40) scale(1.55)"><GardenPlant stage={level.index - 1} progress={1} className="" animate={false} /></g>
      <text x="260" y="96" fontSize="13" fontWeight="700" letterSpacing="3" fill="#276539" fontFamily="Helvetica, Arial, sans-serif">HABIT COMPASS · MY GARDEN</text>
      <text x="260" y="150" fontSize="42" fontWeight="800" fill="#1d2e22">{level.name}</text>
      <text x="260" y="182" fontSize="16" fill="#4f5c53" fontFamily="Helvetica, Arial, sans-serif">Level {level.index} of 10 · {first}'s garden just grew</text>
      <text x="260" y="226" fontSize="15" fontStyle="italic" fill="#276539">“{QUOTES[level.index % QUOTES.length]}”</text>
      <text x="260" y="286" fontSize="12" fill="#94a197" fontFamily="Helvetica, Arial, sans-serif">{new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })} · positive-only progress</text>
    </svg>
  );
}

async function cardBlob(props) {
  const svg = renderToStaticMarkup(<ShareCard {...props} />);
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }));
  const img = new Image();
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  const c = document.createElement("canvas");
  c.width = 1200; c.height = 680;
  c.getContext("2d").drawImage(img, 0, 0, 1200, 680);
  URL.revokeObjectURL(url);
  return new Promise((res) => c.toBlob(res, "image/png"));
}

export function GardenMilestone({ game, user }) {
  const [show, setShow] = useState(null); // {from, level}
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState(false);
  const fired = useRef(false);
  useEffect(() => {
    if (!game) return;
    const seen = game.garden_level_seen;
    if (seen == null) { api.post("/gamification/garden-seen", { index: game.level.index }).catch(() => {}); return; }
    if (game.level.index > seen && !fired.current) {
      fired.current = true;
      setShow({ from: seen, level: game.level });
      setTimeout(() => celebrate(true), 500);
    }
  }, [game]);
  if (!show) return null;
  const first = (user?.name || "").split(" ")[0] || "My";
  const close = () => { setShow(null); api.post("/gamification/garden-seen", { index: show.level.index }).catch(() => {}); };
  const share = async () => {
    setBusy(true);
    try {
      const blob = await cardBlob({ name: user?.name, level: show.level, first });
      const file = new File([blob], `my-garden-${show.level.name.toLowerCase()}.png`, { type: "image/png" });
      const text = `My Root & Rose garden just grew into ${show.level.name} (level ${show.level.index}/10). Positive-only progress.`;
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], text }); }
      else { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = file.name; a.click(); toast.success("Card saved — share it anywhere you like."); }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't build the card right now."); } finally { setBusy(false); }
  };
  const postToCommunity = async () => {
    try {
      await api.post("/wins/share-milestone", { level_index: show.level.index });
      setPosted(true);
      toast.success(`Posted to your community feed — only the stage name, never your habits.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post right now."); }
  };
  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[90] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="garden-milestone" onClick={close}>
        <motion.div className="warm-card relative p-6 sm:p-8 w-full max-w-md text-center" initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ type: "spring", stiffness: 220, damping: 18 }} onClick={(e) => e.stopPropagation()}>
          <ModalClose onClose={close} testid="garden-milestone-x" />
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Your garden grew</div>
          <div className="relative mx-auto w-44 h-56 mt-2">
            <motion.div className="absolute inset-0" initial={{ opacity: 1, scale: 1 }} animate={{ opacity: 0, scale: 0.9 }} transition={{ delay: 0.9, duration: 0.6 }}>
              <GardenPlant stage={show.from - 1} progress={1} className="w-full h-full" animate={false} />
            </motion.div>
            <motion.div className="absolute inset-0" initial={{ opacity: 0, scale: 0.8, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8, type: "spring", stiffness: 160 }}>
              <GardenPlant stage={show.level.index - 1} progress={0} className="w-full h-full" animate={false} />
            </motion.div>
            {[...Array(6)].map((_, i) => (
              <motion.span key={i} className="absolute left-1/2 top-1/2 w-2 h-2 rounded-full bg-amber-400" initial={{ opacity: 0, x: 0, y: 0 }}
                animate={{ opacity: [0, 1, 0], x: Math.cos((i / 6) * Math.PI * 2) * 90, y: Math.sin((i / 6) * Math.PI * 2) * 90 }} transition={{ delay: 1.0 + i * 0.05, duration: 1.2 }} />
            ))}
          </div>
          <h3 className="font-display text-3xl font-extrabold text-stone-900 mt-2" data-testid="garden-milestone-name">{show.level.name}</h3>
          <p className="text-sm text-stone-600 mt-1">Level {show.level.index} of 10. {QUOTES[show.level.index % QUOTES.length]}</p>
          <button onClick={postToCommunity} disabled={posted} data-testid="garden-milestone-community"
            className={`mt-6 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full border ${posted ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"}`}>
            <Users className="w-4 h-4" /> {posted ? "Posted to the community feed" : "Post to community wins (anonymous handle)"}
          </button>
          <div className="flex gap-2 mt-2">
            <button onClick={close} data-testid="garden-milestone-close" className="flex-1 px-4 py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-700">Keep growing</button>
            <button onClick={share} disabled={busy} data-testid="garden-milestone-share" className="flex-1 amber-btn text-sm inline-flex items-center justify-center gap-1.5">
              {navigator.canShare ? <Share2 className="w-4 h-4" /> : <Download className="w-4 h-4" />} {busy ? "Building…" : navigator.canShare ? "Share card" : "Save card"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
